import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus2, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fdate } from "@/lib/api";
import { PageHeader, StatusBadge, Empty, NSelect } from "@/components/common";
import { Button } from "@/components/ui/button";

const OPTIONS = [["nouvelle", "Nouvelle"], ["en_traitement", "En traitement"], ["devis", "Devis établi"], ["cloturee", "Clôturée"]];

const RequestCard = ({ r, onStatus }) => {
  const navigate = useNavigate();
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5" data-testid={`request-card-${r.number}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <span className="font-mono text-xs text-slate-500">{r.number} · {fdate(r.created_at)}</span>
          <h3 className="mt-1 font-display text-lg font-semibold text-slate-900">{r.name}{r.company && <span className="font-normal text-slate-500"> — {r.company}</span>}</h3>
        </div>
        <StatusBadge status={r.status} testId={`request-status-${r.number}`} />
      </div>
      <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-red-600">{r.service_name !== "—" ? r.service_name : "Service non précisé"}</p>
      <p className="mt-3 whitespace-pre-line text-sm text-slate-700">{r.message}</p>
      <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
        <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-slate-900"><Phone className="h-3 w-3" /> {r.phone}</a>
        {r.email && <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-slate-900"><Mail className="h-3 w-3" /> {r.email}</a>}
        {r.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.location}</span>}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
        <NSelect value={r.status} onChange={(v) => onStatus(r, v)} testId={`request-status-select-${r.number}`} className="w-44">
          {OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </NSelect>
        {r.quote_id ? (
          <Button size="sm" variant="outline" onClick={() => navigate(`/devis/${r.quote_id}`)} data-testid={`request-open-quote-${r.number}`}>Voir le devis {r.quote_number}</Button>
        ) : (
          <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => navigate(`/devis/nouveau?demande=${r.id}`)} data-testid={`request-create-quote-${r.number}`}>
            <FilePlus2 className="mr-1 h-3.5 w-3.5" /> Créer un devis
          </Button>
        )}
      </div>
    </div>
  );
};

export default function Requests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => api.get("/requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onStatus = async (r, status) => {
    try { await api.post(`/requests/${r.id}/status`, { status }); toast.success("Statut mis à jour"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const shown = filter ? items.filter((r) => r.status === filter) : items;

  return (
    <div>
      <PageHeader kicker="Gestion commerciale" title="Demandes de prestation" />
      <div className="mb-5 flex flex-wrap gap-2">
        {[["", "Toutes"], ...OPTIONS].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`request-filter-${v || "all"}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filter === v ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>
            {l} {v && `(${items.filter((r) => r.status === v).length})`}
          </button>
        ))}
      </div>
      {shown.length ? (
        <div className="grid gap-4 lg:grid-cols-2">{shown.map((r) => <RequestCard key={r.id} r={r} onStatus={onStatus} />)}</div>
      ) : <Empty testId="requests-empty" text="Aucune demande reçue depuis le site." />}
    </div>
  );
}
